import { useState, useEffect, useCallback } from "react";
import { FaStar } from "react-icons/fa";
import {
  customerTestimonials,
  businessOwnerTestimonials,
} from "../constants/homePageConstant";

const AUTO_INTERVAL_MS = 4500;

function getVisibleCount() {
  if (typeof window === "undefined") return 1;
  const w = window.innerWidth;
  if (w >= 1280) return 3;
  if (w >= 768) return 2;
  return 1;
}

function useVisibleCount() {
  const [visible, setVisible] = useState(getVisibleCount);

  useEffect(() => {
    const onResize = () => setVisible(getVisibleCount());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return visible;
}

function AutoCarousel({ items }) {
  const visible = useVisibleCount();
  const maxIndex = Math.max(0, items.length - visible);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const next = useCallback(() => {
    setIndex((i) => (i >= maxIndex ? 0 : i + 1));
  }, [maxIndex]);

  useEffect(() => {
    if (paused || maxIndex === 0) return undefined;
    const id = setInterval(next, AUTO_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused, maxIndex, next]);

  const trackWidthPercent = (items.length * 100) / visible;
  const slideShiftPercent = items.length > 0 ? 100 / items.length : 0;

  return (
    <div
      className="overflow-hidden w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div
        className="flex items-stretch transition-transform duration-500 ease-in-out"
        style={{
          width: `${trackWidthPercent}%`,
          transform: `translateX(-${index * slideShiftPercent}%)`,
        }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            className="shrink-0 px-1.5 box-border flex"
            style={{ width: `${100 / items.length}%` }}
          >
            <TestimonialCard quote={t.quote} />
          </div>
        ))}
      </div>

      {maxIndex > 0 && (
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show review ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 w-1.5 rounded-full transition-colors ${
                i === index ? "bg-sky-600" : "bg-sky-200"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Testimonials() {
  return (
    <div className="mx-auto max-w-6xl">
      <h2 className="text-lg md:text-2xl font-bold text-[#090914] text-center">
        Don&apos;t just take our word for it
      </h2>

      <div className="mt-5">
        <h3 className="text-base md:text-lg font-semibold text-[#023B64] text-center mb-3">
          Reviews From Customers
        </h3>
        <AutoCarousel items={customerTestimonials} />
      </div>

      <div className="mt-6">
        <h3 className="text-base md:text-lg font-semibold text-[#023B64] text-center mb-3">
          Trusted Businesses in Your Area
        </h3>
        <AutoCarousel items={businessOwnerTestimonials} />
      </div>
    </div>
  );
}

function TestimonialCard({ quote }) {
  return (
    <article className="w-full h-full min-h-[7.5rem] rounded-xl bg-sky-900 text-sky-50 p-4 md:p-5 flex flex-col shadow-sm ring-1 ring-sky-800/40">
      <div className="flex items-center gap-0.5 shrink-0">
        {Array.from({ length: 5 }).map((_, i) => (
          <FaStar key={i} className="h-3 w-3 text-yellow-400" />
        ))}
      </div>
      <p className="mt-2 text-xs md:text-sm leading-relaxed opacity-95 line-clamp-4 flex-1">
        &ldquo;{quote}&rdquo;
      </p>
    </article>
  );
}
